// xadomTableRowSlice.ts
import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { nanoid } from 'nanoid';

import { Cell, Column, Row, RowData, RowsState, ValueType } from '../types';

// Adapter for rows
export const rowsAdapter = createEntityAdapter<Row>();

interface SortAction {
    tableId: string;
    columnId: string;
    orderType: string | undefined;
    valueType: ValueType;
}

// Initial state
const initialState: RowsState = {};

// Slice to manage rows per table
const xadomTableRowSlice = createSlice({
    name: 'xadomTableRow',
    initialState,
    reducers: {
        initializeRows: (
            state,
            action: PayloadAction<{
                tableId: string;
                initialRows?: RowData[];
            }>
        ) => {
            const { tableId, initialRows } = action.payload;

            if (!state[tableId]) {
                state[tableId] = rowsAdapter.getInitialState();
            }

            if (initialRows && initialRows.length > 0) {
                const rowsWithIds = initialRows.map((rowData) => ({
                    id: nanoid(),
                    ...rowData,
                }));
                rowsAdapter.addMany(state[tableId], rowsWithIds);
            }
        },
        addRow: {
            reducer: (state, action: PayloadAction<{ tableId: string; row: Row }>) => {
                const { tableId, row } = action.payload;
                if (!state[tableId]) {
                    state[tableId] = rowsAdapter.getInitialState();
                }
                rowsAdapter.addOne(state[tableId], row);
            },
            prepare: (tableId: string, rowData: Row) => {
                const dataWithEmptyValues = Object.entries(rowData.data).reduce((acc, [key, cell]) => {
                    const isEmpty = cell.value === undefined || cell.value === null || cell.value === '';

                    // value가 비어 있고, 기존 타입이 number인 경우 0, 아니면 ''
                    const defaultValue = isEmpty
                        ? typeof cell.value === 'number' ? 0 : ''
                        : cell.value;

                    acc[key] = { ...cell, value: defaultValue };
                    return acc;
                }, {} as Row['data']);

                const row: Row = {
                    id: rowData.id || nanoid(),
                    data: dataWithEmptyValues,
                };
                return { payload: { tableId, row } };
            }
        },
        addRows: {
            reducer: (state, action: PayloadAction<{ tableId: string; rows: Row[] }>) => {
                const { tableId, rows } = action.payload;
                rowsAdapter.addMany(state[tableId], rows);
            },
            prepare: (tableId: string, rowsData: Row[]) => {
                const rows: Row[] = rowsData.map((rowData) => ({
                    ...rowData,
                    id: rowData.id || nanoid(),
                }));
                return { payload: { tableId, rows } };
            },
        },
        addEmptyRow: (
            state,
            action: PayloadAction<{ tableId: string, columns: { [columnId: string]: Column } }>
        ) => {
            const { tableId, columns } = action.payload;

            if (state[tableId]) {
                // 컬럼 배열을 순회하며 초기 빈 셀 데이터를 설정합니다.
                const emptyData: { [columnId: string]: Cell } = {};

                // isEmpty 플래그 자동 추가
                emptyData['isEmpty'] = { value: true };

                Object.values(columns).forEach((column) => {
                    // 각 컬럼의 타입에 따라 초기값을 설정할 수 있습니다.
                    let initialValue: any = '';
                    switch (column.type) {
                        case ValueType.NUMBER:
                            initialValue = 0; // 숫자 컬럼의 기본값을 0으로 설정
                            break;
                        case ValueType.BOOLEAN:
                            initialValue = false; // Boolean 컬럼의 기본값을 false로 설정
                            break;
                        case ValueType.DATE:
                            initialValue = new Date().toISOString(); // Date 컬럼의 기본값을 현재 날짜로 설정
                            break;
                        case ValueType.STRING:
                        case ValueType.CHECKBOX:
                        default:
                            initialValue = ''; // 기타 컬럼의 기본값은 빈 문자열
                    }
                    emptyData[column.id] = { value: initialValue }; // 초기 셀 데이터 설정
                });

                // 초기화된 빈 데이터를 가진 새 행 추가 (isEmpty: true 자동 설정)
                rowsAdapter.addOne(state[tableId], {
                    id: nanoid(),
                    data: emptyData
                });
            }
        },
        updateRows: (state, action: PayloadAction<{ tableId: string, rows: Row[] }>) => {
            const { tableId, rows } = action.payload;
            if (state[tableId]) {
                rowsAdapter.updateMany(
                    state[tableId],
                    rows.map(row => ({ id: row.id, changes: row }))
                );
            }
        },
        setRows: {
            reducer: (state, action: PayloadAction<{ tableId: string; rows: Row[] }>) => {
                const { tableId, rows } = action.payload;
                rowsAdapter.setAll(state[tableId], rows);
            },
            prepare: (tableId: string, rowsData: Row[]) => {
                const rows: Row[] = rowsData.map((rowData) => ({
                    ...rowData,
                    id: rowData.id || nanoid(),
                }));
                return { payload: { tableId, rows } };
            },
        },
        setRow: {
            reducer: (state, action: PayloadAction<{ tableId: string; rowId: string; row: Row }>) => {
                const { tableId, rowId, row } = action.payload;
                if (state[tableId]) {
                    rowsAdapter.updateOne(state[tableId], { id: rowId, changes: row });
                }
            },
            prepare: (tableId: string, rowId: string, rowData: Row) => {
                const row: Row = {
                    ...rowData,
                    id: rowData.id || nanoid(),
                };
                return { payload: { tableId, rowId, row } };
            },
        },
        setCell: (state, action: PayloadAction<{ tableId: string; rowId: string; columnId: string; cell: Cell }>) => {
            const { tableId, rowId, columnId, cell } = action.payload;
            if (state[tableId]) {
                const row = rowsAdapter.getSelectors().selectById(state[tableId], rowId);
                if (row) {
                    const updatedData = { ...row.data, [columnId]: cell };
                    rowsAdapter.updateOne(state[tableId], { id: rowId, changes: { data: updatedData } });
                }
            }
        },
        updateRow: (state, action: PayloadAction<{ tableId: string; id: string; changes: Partial<Row> }>) => {
            const { tableId, id, changes } = action.payload;
            if (state[tableId]) {
                rowsAdapter.updateOne(state[tableId], { id, changes });
            }
        },
        updateCell: (state, action) => {
            const { tableId, rowId, columnId, cell } = action.payload;
            const tableState = state[tableId];

            if (!tableState) {
                throw new Error(`테이블이 존재하지 않습니다. (tableId: ${tableId})`);
            }

            if (!tableState.entities[rowId]) {
                throw new Error(`행이 존재하지 않습니다. (rowId: ${rowId})`);
            }

            const targetRow = tableState.entities[rowId];
            targetRow.data[columnId] = cell;
        },
        removeRow: (state, action: PayloadAction<{ tableId: string; rowId: string }>) => {
            const { tableId, rowId } = action.payload;
            if (state[tableId]) {
                rowsAdapter.removeOne(state[tableId], rowId);
            }
        },
        clearRows: (state, action: PayloadAction<{ tableId: string }>) => {
            const { tableId } = action.payload;
            if (state[tableId]) {
                rowsAdapter.removeAll(state[tableId]);
            }
        },
        sortRows: (state, action: PayloadAction<SortAction>) => {
            const { tableId, columnId, orderType, valueType } = action.payload;
            if (state[tableId]) {

                if (orderType === undefined) return;

                const allRows = [...rowsAdapter.getSelectors().selectAll(state[tableId])];

                // 빈 행 판단 함수
                const isEmptyRow = (row: any) => {
                    // 1. isEmpty 플래그가 있으면 우선 사용
                    if (row.data.isEmpty?.value === true) {
                        return true;
                    }

                    // 2. isEmpty 플래그가 없는 경우 fallback 로직
                    if (row.data.isEmpty === undefined) {
                        const data = row.data;

                        // ID 필드들 체크 (xxId 패턴)
                        const idFields = Object.keys(data).filter(key =>
                            key.toLowerCase().endsWith('id') &&
                            key !== 'checkbox'
                        );

                        // ID가 비어있으면 빈 행
                        if (idFields.length > 0) {
                            const hasEmptyId = idFields.some(field => {
                                const value = data[field]?.value;
                                return value === '' || value === null || value === undefined;
                            });
                            if (hasEmptyId) return true;
                        }

                        // 핵심 데이터 필드들이 모두 비어있으면 빈 행
                        const coreFields = ['itemName', 'splAmt', 'amount', 'qty', 'name', 'description'];
                        const existingCoreFields = coreFields.filter(field => data[field]);
                        if (existingCoreFields.length > 0) {
                            return existingCoreFields.every(field => {
                                const value = data[field]?.value;
                                return value === '' ||
                                    value === null ||
                                    value === undefined ||
                                    (typeof value === 'string' && value.trim() === '');
                            });
                        }
                    }

                    return false;
                };

                // 빈 행과 데이터 행 분리
                const emptyRows = allRows.filter(isEmptyRow);
                const dataRows = allRows.filter(row => !isEmptyRow(row));

                // 데이터가 있는 행들만 정렬
                const sortedDataRows = dataRows.sort((a, b) => {
                    const cellA = a.data[columnId]?.value;
                    const cellB = b.data[columnId]?.value;

                    // 둘 다 null이거나 undefined인 경우
                    if (cellA == null && cellB == null) return 0;
                    // cellA가 null이면 항상 뒤로
                    if (cellA == null) return 1;
                    // cellB가 null이면 항상 뒤로
                    if (cellB == null) return -1;

                    let comparison = 0;
                    switch (valueType) {
                        case ValueType.NUMBER:
                            comparison = Number(cellA) - Number(cellB);
                            break;
                        case ValueType.PERCENT:
                            // % 기호 제거하고 숫자로 변환
                            const numA = Number(String(cellA).replace('%', ''));
                            const numB = Number(String(cellB).replace('%', ''));
                            comparison = numA - numB;
                            break;
                        case ValueType.DATE:
                            comparison = new Date(cellA).getTime() - new Date(cellB).getTime();
                            break;
                        default: // string
                            if (typeof cellA === 'string' && typeof cellB === 'string' &&
                                (cellA.includes('%') || cellB.includes('%'))) {
                                // % 기호가 있는 경우 숫자로 변환하여 비교
                                const numA = Number(cellA.replace('%', ''));
                                const numB = Number(cellB.replace('%', ''));
                                comparison = numA - numB;
                            } else {
                                comparison = String(cellA).localeCompare(String(cellB));
                            }
                    }

                    return orderType === 'asc' ? comparison : -comparison;
                });

                // 정렬된 데이터 행 + 빈 행 순서로 재배치
                const finalSortedRows = [...sortedDataRows, ...emptyRows];

                rowsAdapter.removeAll(state[tableId]);
                rowsAdapter.setAll(state[tableId], finalSortedRows);
            }
        },
    },

});

export const {
    initializeRows,
    addRow,
    addRows,
    addEmptyRow,
    sortRows,
    updateRows,
    setRows,
    updateRow,
    updateCell,
    removeRow,
    clearRows,
    setRow
} = xadomTableRowSlice.actions;

export default xadomTableRowSlice;